import type { CSSProperties, ReactNode } from "react";
import { colors } from "../design-system";

/**
 * Bords où appliquer la marge de sécurité (encoche, barre home iOS).
 *
 * Conservé à l'identique de la version native, mais le besoin s'est réduit :
 * sur web, le shell applicatif (`app/layout/AppShell`) porte déjà les safe-areas
 * de l'app entière, et au-delà de `md` la notion disparaît complètement
 * (navigateur de bureau).
 *
 * Reste utile pour les écrans affichés HORS shell (connexion, callback
 * d'auth) sur un mobile à encoche.
 */
export type Edge = "top" | "right" | "bottom" | "left";

type ScreenProps = {
  children: ReactNode;
  backgroundColor?: string;
  scroll?: boolean;
  contentContainerStyle?: CSSProperties;
  /**
   * Défaut = aucun bord.
   *
   * ⚠️ CHANGEMENT DE DÉFAUT vs natif, où c'étaient les 4 bords. En natif chaque
   * écran gérait lui-même sa safe-area, d'où la mise en garde d'origine sur le
   * double padding sous la tab bar. Ici le shell s'en charge une fois pour
   * toutes : un écran qui redemanderait les 4 bords produirait précisément le
   * double padding que la version native cherchait à éviter.
   */
  edges?: Edge[];
  className?: string;
};

const INSET: Record<Edge, string> = {
  top: "env(safe-area-inset-top, 0px)",
  right: "env(safe-area-inset-right, 0px)",
  bottom: "env(safe-area-inset-bottom, 0px)",
  left: "env(safe-area-inset-left, 0px)",
};

function insetStyle(edges: Edge[]): CSSProperties {
  return {
    paddingTop: edges.includes("top") ? INSET.top : undefined,
    paddingRight: edges.includes("right") ? INSET.right : undefined,
    paddingBottom: edges.includes("bottom") ? INSET.bottom : undefined,
    paddingLeft: edges.includes("left") ? INSET.left : undefined,
  };
}

export function Screen({
  children,
  backgroundColor = colors.white,
  scroll,
  contentContainerStyle,
  edges = [],
  className,
}: ScreenProps) {
  return (
    <section
      className={["flex h-full min-h-0 flex-col", className].filter(Boolean).join(" ")}
      style={{ backgroundColor, ...insetStyle(edges) }}
    >
      <div
        // `scroll` porte le défilement sur CE conteneur. Sans lui, le contenu
        // qui déborde est géré par le <main> du shell — les deux ne doivent
        // jamais scroller ensemble, sous peine de double barre.
        className={scroll ? "min-h-0 flex-1 overflow-y-auto" : "flex min-h-0 flex-1 flex-col"}
        style={contentContainerStyle}
      >
        {children}
      </div>
    </section>
  );
}
